import { MarketingMaterialsFilterOpts } from "../pages/components/MarketingMaterialsToolbar/MarketingMaterialsToolbar.tsx";
import { models } from "../../../types/api/viewModels.ts";
import { MerchandiseProductFilterRequest } from "../api/getMerchandiseProducts.ts";

interface BuildMerchandiseProductRequestArgs {
	searchValue: string;
	pageNumber: number;
	pageSize: number;
	filterOptions: MarketingMaterialsFilterOpts;
	merchandiseFilters: models["MerchandiseProductFilterDto"];
}

const getSelectedFilterIds = (
	selectedValues: string[] | undefined,
	filters: models["FilterOptionDto"][] | null | undefined
): string[] => {
	if (!selectedValues?.length || !filters?.length) {
		return [];
	}

	const selectedValueSet = new Set(selectedValues);

	return filters
		.filter((filter) =>
			[filter.name, filter.id, filter.code].some(
				(value): value is string => Boolean(value) && selectedValueSet.has(value ?? "")
			)
		)
		.map((filter) => filter.id ?? filter.code)
		.filter((filterId): filterId is string => Boolean(filterId));
};

export const buildMerchandiseProductRequest = ({
	searchValue,
	pageNumber,
	pageSize,
	filterOptions,
	merchandiseFilters
}: BuildMerchandiseProductRequestArgs): MerchandiseProductFilterRequest => {
	const channelIds = getSelectedFilterIds(filterOptions.channel, merchandiseFilters?.channelFilter);
	const availabilityIds = getSelectedFilterIds(filterOptions.availability, merchandiseFilters?.availabilityFilter);
	const categoryIds = getSelectedFilterIds(filterOptions.category, merchandiseFilters?.categoryFilter);
	const typeIds = getSelectedFilterIds(filterOptions.type, merchandiseFilters?.typeFilter);

	const selectedIds = new Set(filterOptions?.brandAndProductLine ?? []);

	const brandAndProductLineFilters =
		merchandiseFilters?.brandAndProductlinesFilter
			?.map((b) => {
				const brandKey = b.id ?? b.code ?? "";
				const brandId = b.id ?? "";

				if (!brandKey) return null;

				const produclines = b.productLines ?? [];

				if (produclines.length === 0) {
					if (!brandId || !selectedIds.has(brandId)) return null;

					return {
						brandKey,
						allProductLine: true,
						productLinesFilter: null
					};
				}

				const selectedProductLines = produclines
					.filter((p) => selectedIds.has(p.id ?? ""))
					.map((p) => p.id ?? p.code)
					.filter((p): p is string => Boolean(p));

				if (selectedProductLines.length === 0) return null;

				const allProductLine = selectedProductLines.length === produclines.length;

				return {
					brandKey,
					allProductLine,
					productLinesFilter: allProductLine ? null : selectedProductLines
				};
			})
			.filter((b): b is NonNullable<typeof b> => Boolean(b)) ?? [];

	return {
		search: searchValue || null,
		pageNumber,
		pageSize,
		selectedChannelKeys: channelIds.length > 0 ? channelIds : null,
		selectedAvailabilityOptions: availabilityIds.length > 0 ? availabilityIds : null,
		selectedCategoryKeys: categoryIds.length > 0 ? categoryIds : null,
		selectedTypeKeys: typeIds.length > 0 ? typeIds : null,
		brandFilterSelection: brandAndProductLineFilters.length > 0 ? brandAndProductLineFilters : null,
		sort: filterOptions.sort
	};
};
